import {Modal} from 'antd'
import {medal_createMedal_Api, medal_deleteMedal_Api, medal_updateMedal_Api,} from 'apis'
import {getId} from 'apis/model/base'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo, useState} from 'react'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import FetchMedalById from './FetchMedalById'
import {CreateMedal, EditMedal, MergeMedal} from './MedalForm'
import {medal_assignNewMedalToStudentByMerging_Api} from "../../../apis";
import LoadingBlock from "../../Loading/LoadingBlock";

const EditForm = ({ medal_id, ...props }) => (
  <FetchMedalById id={medal_id}>
    {({ item, isLoading }) => {
      if (isLoading) {
        return <LoadingBlock />
      }
      return (
        <EditMedal
          {...props}
          item={item}
        />
      )
    }}
  </FetchMedalById>
)

export const useMedalManager = ({
  owner_id,
  owner_type,
  onCreated = NullAsDefault,
  onUpdated = NullAsDefault,
  onDeleted = NullAsDefault,
  autoRefresh = true,
} = {}) => {
  const t = useTranslate()

  const [refreshKey, setRefreshKey] = useState()

  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const onCancel = () => setAction({})

  const onError = notifyOnError(t)

  const onRefresh = () => {
    if (autoRefresh) {
      setRefreshKey(Date.now())
    }
    onCancel()
  }

  const createAsync = useAsyncAction({
    onError,
    apiInfo: medal_createMedal_Api,
    query: bindQueryParams([{ id: owner_id }, { prop: owner_type }]),
    onSuccess: (...args) => {
      successNotify(NotificationActionTypes.CREATE, t)
      onRefresh()
      onCreated(...args)
    },
  })

  const updateAsync = useAsyncAction({
    onError,
    apiInfo: medal_updateMedal_Api,
    onSuccess: (...args) => {
      successNotify(NotificationActionTypes.UPDATE, t)
      onCancel()
      onUpdated(...args)
    },
  })

  const deleteAsync = useAsyncAction({
    onError,
    apiInfo: medal_deleteMedal_Api,
    onSuccess: (...args) => {
      successNotify(NotificationActionTypes.DELETE, t)
      onRefresh()
      onDeleted(...args)
    },
  })

  const assignNewMedalToStudentByMergingAsync = useAsyncAction({
    onError,
    apiInfo: medal_assignNewMedalToStudentByMerging_Api,
    query: bindQueryParams([{ id: owner_id }, { type: owner_type }]),
    onSuccess: (...args) => {
      successNotify(NotificationActionTypes.UPDATE, t)
      onRefresh()
      onCreated(...args)
    },
  })

  const { handleAsyncAction: onDelete } = deleteAsync ?? {}
  const { handleAsyncAction: onCreate, ...creatingAsync } = createAsync ?? {}
  const { handleAsyncAction: onUpdate, ...updatingAsync } = updateAsync ?? {}
  const { handleAsyncAction: assignNewMedalToStudentByMerging, ...mergingModalOnStudentAsync } = assignNewMedalToStudentByMergingAsync ?? {}

  const handleAction = (type, params) => {
    const id = getId(params)
    switch (type) {
      case CRUD.CREATE:
      case CRUD.UPDATE:
      case "MERGE_MEDAL_ON_STUDENT":
        setAction({
          type,
          value: params,
        })
        break
      case CRUD.DELETE:
        showDeleteConfirmDialog({
          translate: t,
          onOk: () => {
            onDelete({}, { id })
          },
        })
        break
      default:
        break
    }
  }

  const modal = useMemo(() => {
    const { type, value } = action ?? {}
    let modal_params = {}
    let params = {
      onSubmit: NullAsDefault,
      owner_id,
      owner_type
    }
    let Component = NullAsDefault
    const id = getId(value)
    switch (type) {
      case CRUD.CREATE:
        modal_params = {
          title: (
            <div className="text-center font-bold text-color-000 uppercase tracking-wide">
              {t('create certification')}
            </div>
          ),
        }
        params = {
          onSubmit: (values) => {
            onCreate(values)
          },
          isSubmitting: creatingAsync.isLoading,
        }
        Component = CreateMedal
        break
      case CRUD.UPDATE:
        modal_params = {
          title: (
            <div className="text-center font-bold text-color-000 uppercase tracking-wide">
              {t('edit certification')}
            </div>
          ),
        }
        params = {
          medal_id: id,
          onSubmit: (values) => {
            onUpdate(values, {
              id,
            })
          },
          isSubmitting: updatingAsync.isLoading,
        }
        Component = EditForm
        break
      case "MERGE_MEDAL_ON_STUDENT":
        modal_params = {
          title: (
              <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                {t('medal merging')}
              </div>
          ),
        }
        params = {
          type: "student",
          owner_id,
          owner_type,
          onSubmit: (values) => {
            assignNewMedalToStudentByMerging(values)
          },
          isSubmitting: mergingModalOnStudentAsync.isLoading,
        }
        Component = MergeMedal
        break
      default:
        return null
    }
    return (
      <Modal
        width={600}
        footer={null}
        onCancel={onCancel}
        destroyOnHidden={true}
        open={action?.type}
        className="custom-modal"
        {...modal_params}>
        <Component
          {...params}
          {...{ onCancel }}
        />
      </Modal>
    )
  }, [action, creatingAsync.isLoading, updatingAsync.isLoading])

  return {
    modal,
    onCreate,
    onUpdate,
    onDelete,
    assignNewMedalToStudentByMerging,
    refreshKey,
    createAsync,
    updateAsync,
    deleteAsync,
    mergingModalOnStudentAsync,
    handleAction,
  }
}
